import React, {
  createContext,
  FC,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  deleteUsersInSuscription,
  getPlanById,
  getSuscriptionById,
  getUsersByOrgId,
  getUsersInSuscription,
  postUsersInSuscription,
} from "../../api/broker";
import { useAuthContext } from "../../hooks/Auth";
import { SingleSuscriptContextProps } from "../../interfaces/SuperAdmin/ContextProps/iSingleSuscription";
import { iDataPlan } from "../../interfaces/SuperAdmin/Pages/iDataPlan";
import { iDataSuscripcionOrg } from "../../interfaces/SuperAdmin/Pages/iDataSuscripcion";
import { iDataUser } from "../../interfaces/SuperAdmin/Pages/iDataUser";
import {
  iDataUserSuscription,
  UserSuscriptionFields,
} from "../../interfaces/SuperAdmin/Pages/iDataUserSuscription";

const SingleSuscriptContext = createContext<SingleSuscriptContextProps>(
  {} as SingleSuscriptContextProps
);

interface Props {
  children: React.ReactNode;
}

const SingleSuscriptProvider: FC<Props> = ({ children }) => {
  const { updateCreditInfo, user } = useAuthContext();

  const [suscripcion, setSuscripcion] = useState<iDataSuscripcionOrg>(
    {} as iDataSuscripcionOrg
  );
  const [allUsers, setAllUsers] = useState<iDataUser[]>([]);
  const [suscribedUsers, setSuscribedUsers] = useState<iDataUserSuscription[]>(
    []
  );
  const [plan, setPlan] = useState<iDataPlan>({} as iDataPlan);
  const [selectedSuscribedUsers, setSelectedSuscribedUsers] = useState<
    string[]
  >([]);
  const [searchText, setSearchText] = useState("");

  const [isLoadingSuscr, setIsLoadingSuscr] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { idSuscription } = useParams();

  useEffect(() => {
    const fetchSuscr = async () => {
      if (idSuscription) {
        setIsLoadingSuscr(true);
        try {
          const susRes = await getSuscriptionById(idSuscription);

          if (susRes.data.statusCode === 200) {
            setSuscripcion(susRes.data.body);

            //Se extrae el plan tambien para el max de usuarios
            const [planRes, usersSuscrRes] = await Promise.all([
              getPlanById(susRes.data.body.id_plan),
              getUsersInSuscription(susRes.data.body.id_suscripcion),
            ]);
            console.log(usersSuscrRes);

            if (planRes.data.statusCode === 200) {
              setPlan(planRes.data.body);
            } else {
              toast.error("Error al obtener el plan");
            }
            if (usersSuscrRes.data.statusCode === 200) {
              setSuscribedUsers(usersSuscrRes.data.body);
            } else {
              toast.error("Error al obtener los usuarios de la suscripción");
            }
          }
        } catch (error) {
          console.log(error);
          toast.error(
            "Error al obtener el plan y/o los usuarios de la suscripción"
          );
        } finally {
          setIsLoadingSuscr(false);
        }
      }
    };
    fetchSuscr();
  }, [idSuscription]);

  const fetchAllUsers = async () => {
    const id = suscripcion?.id_organizacion;
    if (id) {
      setIsLoadingUsers(true);
      try {
        const res = await getUsersByOrgId(id);
        if (res.data.statusCode === 200) {
          setAllUsers(res.data.body);
        } else {
          toast.error("Error al obtener los usuarios de la organización");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener los usuarios de la organización");
      } finally {
        setIsLoadingUsers(false);
      }
    }
  };

  const toggleAddModal = (isOpen: boolean) => {
    if (isOpen && !allUsers.length) {
      fetchAllUsers();
    }
    setIsAddModalOpen(isOpen);
  };

  const toggleDeleteModal = (isOpen: boolean) => {
    setIsDeleteModalOpen(isOpen);
  };

  // En caso de abrir el modal de agregar usuarios, hacemos fetch
  useEffect(() => {
    if (isAddModalOpen) {
      fetchAllUsers();
    }
  }, [isAddModalOpen]);

  // Quitar usuarios de la suscripción
  const deleteSelections = async (body: UserSuscriptionFields) => {
    setIsDeleting(true);
    try {
      const res = await deleteUsersInSuscription(body);
      console.log(res);
      if (res.data.statusCode === 200) {
        setSelectedSuscribedUsers([]);
        setSuscribedUsers((prev) =>
          prev.filter((item) => !body.id_usuarios.includes(item.id_usuario))
        );
        toggleDeleteModal(false);
        toast.success("Usuarios eliminados de la suscripción exitosamente");
        /* Verificamos si la suscripción que se agregó es del usuario que estamos usando */
        if (user && body.id_usuarios.includes(user.id)) {
          updateCreditInfo();
        }
      } else {
        toast.error("Error al eliminar los usuarios de la suscripción");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al eliminar los usuarios de la suscripción");
    } finally {
      setIsDeleting(false);
    }
  };

  // Agregar usuarios a suscripcion
  const submitSelections = async (body: UserSuscriptionFields) => {
    setIsAdding(true);
    try {
      const res = await postUsersInSuscription(body);
      console.log(res);
      if (res.data.statusCode === 201) {
        setSuscribedUsers((prev) => {
          return [...prev, ...res.data.body];
        });
        setIsAddModalOpen(false);
        toast.success("Usuarios agregados a la suscripción exitosamente");

        /* Verificamos si la suscripción que se agregó es del usuario que estamos usando */
        if (user && body.id_usuarios.includes(user.id)) {
          updateCreditInfo();
        }
      } else {
        toast.error("Error al agregar los usuarios a la suscripción");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al agregar los usuarios a la suscripción");
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <SingleSuscriptContext.Provider
      value={{
        suscripcion,
        plan,
        suscribedUsers,
        allUsers,
        selectedSuscribedUsers,
        searchText,
        isLoadingSuscr,
        isLoadingUsers,
        isAdding,
        isDeleting,
        isAddModalOpen,
        isDeleteModalOpen,
        setSelectedSuscribedUsers,
        toggleAddModal,
        toggleDeleteModal,
        deleteSelections,
        submitSelections,
        setSearchText,
      }}
    >
      {children}
    </SingleSuscriptContext.Provider>
  );
};

const useSuperAdminSingleSuscrip = () => useContext(SingleSuscriptContext);

export { SingleSuscriptProvider, useSuperAdminSingleSuscrip };

